import DashboardLayout from '../../layouts/LayoutContainers/DashboardLayout'
import './styles.css'

// Hooks
import useDashboard from './hooks/useDashboard'
import { useMediaQuery } from 'react-responsive'
import { useState, useCallback, useMemo } from 'react'

// Components
import Dropdowns from './components/Dropdowns'
import Chart from 'react-apexcharts'
import StatisticCardSkeleton from './components/Skeletons/StatisticCardSkeleton'
import PieChartSkeleton from './components/Skeletons/PieChartSkeleton'
import BarchartSkeleton from './components/Skeletons/BarchartSkeleton'
import { NavLink } from 'react-router-dom'
import StatisticCard from './components/StatisticCard'
import { colors } from './constants'
// Icons
import { FaClock } from 'react-icons/fa6'
import { LuBadgePercent } from 'react-icons/lu'
import { MdOutlineSubject } from 'react-icons/md'
import { HiMiniReceiptPercent } from 'react-icons/hi2'
import { BsFillLightbulbFill } from 'react-icons/bs'
import { FaCheckCircle } from 'react-icons/fa'
import { IoIosWarning } from 'react-icons/io'
import { useMaterialUIController } from '../../context'
import Button from '../../components/Button'
import CustomTour from '../../components/CustomTour'

const Dashboard = () => {
  const [controller] = useMaterialUIController()
  const { theme } = controller
  const [selectedCourse, setSelectedCourse] = useState('all')
  const [period, setPeriod] = useState('weekly')
  const [chapter, setChapter] = useState('all')
  const { data, isLoading } = useDashboard(period, chapter, selectedCourse)
  const isSmallDevice = useMediaQuery({
    query: '(max-width: 800px)',
  })
  const labelsTextColor = useMemo(
    () => (theme === 'dark' ? '#ffffff' : '#000e0c'),
    [theme],
  )
  const showBarChart = !(
    data?.barChart?.subjectsValue.correctSubjects?.every(
      element => element === 0,
    ) &&
    data?.barChart?.subjectsValue.incorrectSubjects?.every(
      element => element === 0,
    ) &&
    data?.barChart?.gridsValue.correctGrids?.every(element => element === 0) &&
    data?.barChart?.gridsValue.incorrectGrids?.every(element => element === 0)
  )
  const showPieChart = !data?.pieChartData?.every(
    element => element.count === 0,
  )

  const handlePeriodValue = useCallback(event => {
    setPeriod(event.target.value)
  }, [])

  const handleSelectedCourse = useCallback(event => {
    setSelectedCourse(event.target.value)
    setChapter('all')
  }, [])

  const handleChapterValue = useCallback(event => {
    setChapter(event.target.value)
  }, [])

  const isBelowMinimumGrids = useMemo(() => data?.gridsSolved < 10, [data])

  const dataForHeader = useMemo(
    () => [
      {
        icon: <FaCheckCircle />,
        label: 'Grile rezolvate',
        value: `${data?.gridsSolved} (${data.correctGridsSolved?.percent} corecte)`,
        iconBackgroundColor: 'rgb(var(--stats-grile))',
        iconColor: 'white',
      },

      {
        icon: <MdOutlineSubject />,
        label: 'Simulări rezolvate',
        value: data?.subjectSolved,
        iconBackgroundColor: 'rgb(var(--stats-simulari))',
        iconColor: 'white',
      },
      {
        icon: <IoIosWarning />,
        label: `${
          isBelowMinimumGrids
            ? 'Rezolvă minim 10 grile pentru a vedea cel mai slab capitol'
            : 'Cel mai slab capitol'
        }`,
        value: ` ${
          isBelowMinimumGrids
            ? `Cel mai slab capitol`
            : data?.chapterYouShouldWork
        }`,
        iconBackgroundColor: `${
          isBelowMinimumGrids
            ? 'rgb(var(--stats-capitol-criteria-not-met))'
            : 'rgb(var(--stats-capitol))'
        }`,
        iconColor: 'white',
      },
      {
        icon: <BsFillLightbulbFill />,
        label: 'Indicii Folosite',
        value: data?.hintsUsed,
        iconBackgroundColor: 'rgb(var(--stats-hint))',
        iconColor: 'white',
      },
      {
        icon: <FaClock />,
        label: 'Timpul mediu de rezolvare',
        value: data?.averageTime,
        iconBackgroundColor: 'rgb(var(--stats-time))',
        iconColor: 'white',
      },
      {
        icon: <HiMiniReceiptPercent />,
        label: 'Procent mediu rezolvare simulare',
        value: data?.subjectAverageCorrect,
        iconBackgroundColor: 'rgb(var(--stats-rezolvare))',
        iconColor: 'white',
      },
    ],
    [data, isBelowMinimumGrids],
  )

  const series = useMemo(
    () => [
      {
        name: 'Grile corecte din simulări',
        data: data?.barChart?.subjectsValue.correctSubjects,
        group: 'gridsFromSubjects',
      },
      {
        name: 'Grile gresite din simulări',
        data: data?.barChart?.subjectsValue.incorrectSubjects,
        group: 'gridsFromSubjects',
      },
      {
        name: 'Grile corecte',
        data: data?.barChart?.gridsValue?.correctGrids,
        group: 'grids',
      },
      {
        name: 'Grile gresite',
        data: data?.barChart?.gridsValue?.incorrectGrids,
        group: 'grids',
      },
      // {
      //   name: 'Media Platformei',
      //   data: data?.averages,
      //   group: 'averages'
      // }
    ],
    [data],
  )

  const options = useMemo(
    () => ({
      chart: {
        stacked: true,
        type: 'bar',
        animations: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          columnWidth: isSmallDevice || period === 'monthly' ? '100%' : '65%',
          distributed: false,
        },
      },
      xaxis: {
        categories: data?.barChart?.xAxis,
        labels: {
          style: {
            colors: labelsTextColor,
          },
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: labelsTextColor,
          },
        },
        forceNiceScale: true,
      },
      legend: {
        show: true,
        labels: {
          colors: 'rgb(var(--over-ternary-background))',
        },
      },
      colors: ['#635594', '#332859', '#3ff2d1', '#01826a'],
      dataLabels: {
        enabled: true,
        formatter(val) {
          return val === 0 ? '' : val
        },
        style: {
          colors: ['#fff'],
        },
      },
    }),
    [data, period, isSmallDevice, labelsTextColor],
  )

  const pieSeries = useMemo(
    () =>
      data &&
      data?.pieChartData?.map(element => {
        return element?.count
      }),
    [data],
  )

  const labels = useMemo(
    () =>
      data?.pieChartData?.map(element => {
        return element?.chapter
      }),
    [data],
  )

  const pieOptions = useMemo(
    () => ({
      chart: {
        type: 'pie',
      },
      colors,

      stroke: {
        show: true,
        width: 1,
      },

      donut: {
        size: '100%',
      },
      legend: {
        position: 'bottom',
        labels: {
          colors: 'rgb(var(--over-ternary-background))',
        },
      },
      labels,
    }),
    [labels],
  )

  const statisticHeader = useMemo(() => {
    if (isLoading) {
      return Array(dataForHeader.length)
        .fill()
        .map((_, index) => <StatisticCardSkeleton key={index} />)
    } else {
      return dataForHeader.map((card, index) => (
        <StatisticCard
          key={`statisticCard-${index}`}
          icon={card.icon}
          label={card.label}
          value={card.value}
          iconBackgroundColor={card.iconBackgroundColor}
        />
      ))
    }
  }, [isLoading, dataForHeader])

  return (
    <DashboardLayout>
      <div className="dashboard-container flex justify-center flex-col pb-[25px]">
        <Dropdowns
          selectedCourse={selectedCourse}
          handleSelectedCourse={handleSelectedCourse}
          chapter={chapter}
          period={period}
          handleChapterValue={handleChapterValue}
          handlePeriodValue={handlePeriodValue}
        />
        <div>
          <div className="dashboard-header-cards">{statisticHeader}</div>
        </div>
        <div
          className={`w-full mt-3 gap-3 grid grid-cols-1 lg:grid-cols-2  ${
            isLoading ? 'page-loading' : ''
          }`}
        >
          <div
            className={`bg-secondary-background rounded  p-3  ${
              isLoading ? 'min-h-[300px] md:min-h-[500px]' : ''
            } ${
              !showBarChart
                ? 'flex justify-center items-center min-h-[300px]'
                : ''
            }`}
          >
            {isLoading ? (
              <BarchartSkeleton />
            ) : showBarChart ? (
              <Chart
                options={options}
                series={series}
                type="bar"
                height={isSmallDevice ? 300 : ''}
              />
            ) : (
              <div className="flex text-over-primary-background flex-col justify-center items-center gap-3">
                <span>Nu ai rezolvat nicio grilă</span>{' '}
                <NavLink to="/grile">
                  <Button className="bg-btn-primary">Către grile</Button>
                </NavLink>
              </div>
            )}
          </div>
          <div className="flex justify-center items-center bg-secondary-background rounded grow min-h-[300px]">
            {isLoading ? (
              <PieChartSkeleton />
            ) : showPieChart ? (
              <Chart
                width={isSmallDevice ? '400' : '500'}
                options={pieOptions}
                series={pieSeries}
                type="pie"
              />
            ) : (
              <div className="flex flex-col text-over-primary-background justify-center items-center gap-3">
                <span>Nu ai rezolvat nicio simulare</span>{' '}
                <NavLink to="/subiect">
                  <Button className="bg-btn-primary">Către simulări</Button>
                </NavLink>
              </div>
            )}
          </div>
        </div>
        <CustomTour isOpen={true} />
      </div>
    </DashboardLayout>
  )
}

export default Dashboard
